<template>
  <template v-if="items.length > 0">
    <button
      type="button"
      class="btn btn-sm btn-icon admin-actions-toggle"
      aria-haspopup="true"
      :aria-controls="menuId"
      title="Admin"
      :disabled="busy"
      @click="menu?.toggle($event)"
    >
      <FontAwesomeIcon
        :icon="busy ? 'spinner' : ['fas', 'user-shield']"
        :class="{ 'fa-spin': busy }"
      />
    </button>
    <Menu
      :id="menuId"
      ref="menu"
      :model="items"
      popup
      :pt="{
        root: { class: 'dropdown-menu show' },
        list: { class: 'list-unstyled mb-0' },
      }"
    >
      <template #item="{ item, props: itemProps }">
        <a v-bind="itemProps.action" class="dropdown-item" :class="item.class">
          {{ item.label }}
        </a>
      </template>
    </Menu>
  </template>
</template>

<script setup lang="ts">
import type { MenuItem } from "primevue/menuitem";
import Menu from "primevue/menu";
import { computed, useId, useTemplateRef } from "vue";
import type { CompanyOrder } from "../common/companyOrderTypes";

/**
 * Admin-only actions on an order, listing only those the server accepts on it now (`allowed_actions`);
 * renders nothing when it accepts none. The menu is a popup attached to the page body, so a virtually
 * scrolled table cannot clip it.
 */
const props = defineProps<{
  order: CompanyOrder;
  busy: boolean;
}>();

const emit = defineEmits<{
  "update:status": [status: "COMPLETED" | "CANCELLED"];
  retry: [];
}>();

const menu = useTemplateRef<InstanceType<typeof Menu>>("menu");
const menuId = useId();

const items = computed<MenuItem[]>(() => {
  const allowed = props.order.allowed_actions;
  return [
    ...(allowed.includes("ADMIN_COMPLETE")
      ? [
          {
            label: "Mark as completed",
            command: () => emit("update:status", "COMPLETED"),
          },
        ]
      : []),
    ...(allowed.includes("RETRY")
      ? [{ label: "Retry order", command: () => emit("retry") }]
      : []),
    ...(allowed.includes("ADMIN_CANCEL")
      ? [
          {
            label: "Cancel order",
            class: "text-danger",
            command: () => emit("update:status", "CANCELLED"),
          },
        ]
      : []),
  ];
});
</script>

<style scoped lang="scss">
.admin-actions-toggle {
  background: var(--phoenix-warning-bg-subtle);
  border: 1px dashed var(--phoenix-warning-border-subtle);
}
</style>
